import animate from "animate";
import type EventEmitter from "eventemitter3";
import Stats from "stats.js";
import lottie from "lottie-web";
import SmoothScroll from "smooth-scroll";
import { FPS, IS_DEBUG } from "./_constants";
import PakutasoGallery from "./_PakutasoGallery";
import { zeroPadding } from "./_string_utils";
import ParallaxImage from "./_ParallaxImage";


const { $, gsap } = window;

interface Size {
  width: number;
  height: number;
}

export default class App {
  emitter: EventEmitter;

  previousSize: Size | null;

  previousScrollTop: number | null;

  stats?: Stats;

  animation?: ReturnType<typeof animate>;

  pakutasoGallery?: PakutasoGallery;

  timelineHero?: gsap.core.Timeline;

  smoothScroll?: SmoothScroll;

  parallaxImageList: ParallaxImage[] = [];

  constructor(emitter: EventEmitter) {
    this.emitter = emitter;
    this.previousSize = null;
    this.previousScrollTop = null;
    this.setupStats();
    this.setupSmoothScroll();
    this.setupPakutasoGallery();
    this.setupHero();
    this.setupParallax();
    this.setupScrollTrigger();
    this.setupEvents();
  }

  setupStats() {
    this.stats = new Stats();
    const $dom = $(this.stats.dom);
    $dom.attr("id", "Stats");
    $("body").append($dom);
    $("#Stats").css({ left: "auto", right: 0 });
    $("#Stats").toggle(IS_DEBUG);
  }

  setupSmoothScroll() {
    this.smoothScroll = new SmoothScroll('a[href*="#"]', {
      speed: 1000,
      speedAsDuration: true,
      updateURL: false,
    });
  }

  setupPakutasoGallery() {
    const { smoothScroll } = this;
    if (!smoothScroll) {
      throw new Error("smoothScroll is null");
    }

    const pakutasoGalleryContainer = document.querySelector(".p-pakutaso-gallery");
    if (!pakutasoGalleryContainer) {
      throw new Error("pakutasoGalleryContainer is null");
    }
    this.pakutasoGallery = new PakutasoGallery(pakutasoGalleryContainer, smoothScroll);
  }

  // eslint-disable-next-line class-methods-use-this
  setupHero() {
    const logoContainer = document.querySelector(".p-section-hero__logo");
    if (!logoContainer) {
      throw new Error("logoContainer is null");
    }

    const animationItem = lottie.loadAnimation({
      container: logoContainer,
      loop: false,
      autoplay: false,
      renderer: 'svg',
      // path: "./assets/img/hero/tadaima-fukushima-logo.json",
      // path: "./assets/img/hero/tadaima-fukushima-logo-narration.json",
      path: "./assets/img/hero/tadaima-fukushima-logo-stroke.json",
    });
    const timelineHero = gsap.timeline({ paused: true });
    this.timelineHero = timelineHero;
    timelineHero.to(".p-section-hero__loading-cover", {
      opacity: 0,
      duration: 1,
    }, 0);
    timelineHero.from(".p-section-hero__title", {
      opacity: 0,
      duration: 1,
    }, 0.5);
    timelineHero.add(() => {
      animationItem.play();
    }, 0.5);


    animationItem.addEventListener("data_ready", () => {
      timelineHero.play();
    });

  }

  setupParallax() {

    // statement

    this.parallaxImageList = [];



    const backgroundMakingHeightVh = 200;

    interface ScrollParams {
      target: string,
      trigger: string,
      farRatio: number,
    }

    const MIDDLE = 0.1;
    const FAR = 0.3;


    const scrollParamsList: ScrollParams[] = [
      {
        target: ".p-section-statement__image--01",
        trigger: ".p-section-statement__parallax-area--01",
        farRatio: MIDDLE,
      },
      {
        target: ".p-section-statement__image--02",
        trigger: ".p-section-statement__parallax-area--02",
        farRatio: FAR,
      },
      {
        target: ".p-section-statement__image--03",
        trigger: ".p-section-statement__parallax-area--03",
        farRatio: MIDDLE,
      },
      {
        target: ".p-section-statement__image--04",
        trigger: ".p-section-statement__parallax-area--04",
        farRatio: FAR,
      },
      {
        target: ".p-section-statement__image--05",
        trigger: ".p-section-statement__parallax-area--05",
        farRatio: MIDDLE,
      },
      {
        target: ".p-section-statement__image--06",
        trigger: ".p-section-statement__parallax-area--06",
        farRatio: MIDDLE,
      },
      {
        target: ".p-section-statement__image--07",
        trigger: ".p-section-statement__parallax-area--07",
        farRatio: FAR,
      },
      {
        target: ".p-section-statement__image--08",
        trigger: ".p-section-statement__parallax-area--08",
        farRatio: FAR,
      },
      {
        target: ".p-section-statement__image--09",
        trigger: ".p-section-statement__parallax-area--09",
        farRatio: MIDDLE,
      },
      {
        target: ".p-section-statement__image--10",
        trigger: ".p-section-statement__parallax-area--10",
        farRatio: FAR,
      },
      {
        target: ".p-section-statement__image--11",
        trigger: ".p-section-statement__parallax-area--11",
        farRatio: MIDDLE,
      },
    ];
    for (const scrollParams of scrollParamsList) {
      const { target, trigger, farRatio } = scrollParams;
      // const fromY = `${(triggerHeightVh + 100) / 2 * farRatio * -1}vh`;
      // const toY = `${(triggerHeightVh + 100) / 2 * farRatio}vh`;
      const targetElement = document.querySelector(target) as HTMLElement;
      if (!targetElement) {
        throw new Error("targetElement is null");
      }
      const triggerElement = document.querySelector(trigger) as HTMLElement;
      if (!triggerElement) {
        throw new Error("triggerElement is null");
      }

      const parallaxImage = new ParallaxImage(targetElement, triggerElement, farRatio);
      this.parallaxImageList.push(parallaxImage);

    }



  }

  // eslint-disable-next-line class-methods-use-this
  setupScrollTrigger() {


    // making
    for (let number = 1; number <= 20; number += 1) {
      gsap.from(`.p-section-making__image--${zeroPadding(number, 2)}`,
        {
          delay: 1.5 * Math.random(),
          opacity: 0,
          ease: "none",
          duration: 1.5,
          scrollTrigger: {
            trigger: `.p-section-making__image--${zeroPadding(number, 2)}`,
            start: "top bottom",
            markers: IS_DEBUG,
          }
        }
      );

    }
    {
      const modifiers = [];
      modifiers.push("--title");
      for (let number = 1; number <= 3; number += 1) {
        modifiers.push(`--${zeroPadding(number, 2)}`);
      }

      for (const modifier of modifiers) {
        gsap.from(`.p-section-making__scene-box${modifier}`,
          {
            delay: 0.5,
            opacity: 0,
            ease: "none",
            duration: 1,
            scrollTrigger: {
              trigger: `.p-section-making__scene-box${modifier}`,
              start: "top bottom",
              markers: IS_DEBUG,
            }
          }
        );

      }
    }

  }


  setupEvents() {
    const { emitter } = this;
    emitter.on("resize", this.onResize, this);
    this.onResize();
    emitter.on("scroll", this.onScroll, this);
    emitter.on("tick", this.onTick, this);

    this.animation = animate(() => {
      if (this.stats) {
        this.stats.begin();
      }
      this.checkEvents();
      emitter.emit("tick");
      if (this.stats) {
        this.stats.end();
      }
    }, FPS);
  }

  // eslint-disable-next-line class-methods-use-this
  getSize() {
    return {
      width: window.innerWidth,
      height: window.innerHeight,
    };
  }

  checkResize() {
    const { emitter } = this;
    const size = this.getSize();
    if (
      this.previousSize != null &&
      this.previousSize.width === size.width &&
      this.previousSize.height === size.height
    ) {
      return;
    }
    this.previousSize = size;
    emitter.emit("resize");
  }

  checkScroll() {
    const { emitter } = this;
    const scrollTop = $(window).scrollTop();
    if (
      this.previousScrollTop != null &&
      this.previousScrollTop === scrollTop
    ) {
      return;
    }
    this.previousScrollTop = scrollTop;
    emitter.emit("scroll");
  }

  checkEvents() {
    this.checkResize();
    this.checkScroll();
  }

  // eslint-disable-next-line
  onScroll() { }

  // eslint-disable-next-line
  onResize() { }

  onTick() {
    for (const parallaxImage of this.parallaxImageList) {
      parallaxImage.update();
    }
  }

  static load() {
    const promises: Promise<any>[] = [];
    return Promise.all(promises);
  }
}
